import { useStytchMemberSession } from "@stytch/react/b2b";

/**
 * Stytch Role ID for the middle tier. Must match MANAGER_ROLE_ID on the server
 * and the Role created in the Stytch Dashboard.
 */
export const MANAGER_ROLE_ID = "maru_manager";

export type PermissionLevel = "admin" | "manager" | "member";

export interface UsePermissionLevelResult {
  level: PermissionLevel;
  isAdmin: boolean;
  isManagerOrAbove: boolean;
}

/**
 * Reads the live Stytch member session every render — deliberately no
 * useState/useEffect/useMemo. useStytchMemberSession() is reactive, so a
 * session refresh re-renders every consumer and role changes propagate without
 * a manual subscription.
 *
 * Two properties of the Stytch payload shape this:
 *   - `stytch_member` is implicit on everyone, so member is the fall-through
 *     case and never a positive test.
 *   - The roles array is not privilege-ordered, so admin must be checked first.
 *
 * Frontend gating is UX convenience. The security boundary is the server-side
 * MinLevelGuard. Components should fail closed, but must never treat the
 * absence of a check here as a guarantee.
 */
export function usePermissionLevel(): UsePermissionLevelResult {
  const { session } = useStytchMemberSession();
  const roles = session?.roles ?? [];

  const level: PermissionLevel = roles.includes("stytch_admin")
    ? "admin"
    : roles.includes(MANAGER_ROLE_ID)
      ? "manager"
      : "member";

  return {
    level,
    isAdmin: level === "admin",
    isManagerOrAbove: level === "admin" || level === "manager",
  };
}
